import clsx from "clsx";
import { cloneDeep } from "lodash-es";
import { FC, useEffect, useState } from "react";

import { downloadJsonFile } from "../../../../../utils/file";
import { updateSection, useSectionSettingActions } from "../../../../../utils/section";

import { AdminSectionListItem } from "./AdminSectionListItem";

import { serverCloneSection, serverExportSection } from "api/api-server-actions";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi, useAppDispatch } from "store/index";
import { OrderedSectionType } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";

type AdminSectionListPanelProps = {
  title: string;
  sections: OrderedSectionType[];
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  canEdit: boolean;
  isDisabled?: boolean;
};

export const AdminSectionListPanel: FC<AdminSectionListPanelProps> = ({
  title,
  sections,
  eventPrefix,
  eventProperties,
  canEdit,
  isDisabled = false,
}) => {
  const [sectionList, setSectionList] = useState<OrderedSectionType[]>(sections);
  // const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const { handleSectionDelete } = useSectionSettingActions();
  const { sortByActiveTitles } = useIsArchived();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setSectionList(sections.map(cloneDeep).toSorted(sortByActiveTitles));
  }, [sections, sortByActiveTitles]);

  const handleClone = async (sectionId: string, courseId?: string, termId?: string) => {
    try {
      await serverCloneSection(sectionId, courseId, termId);

      toastSuccessMessage(t("success.toasts.clone_section_started_success"));
    } catch (error) {
      toastLocalizedOperationCatcher("copy_section_failure")(error);
    }
  };

  const handleSectionSettings = (sectionId: string) => {
    dispatch(viewStateActions.setSectionSettingsForSectionId(sectionId));
  };

  const handleExportSection = (section: SectionType) => async () => {
    const sectionJson = await serverExportSection(section.id);
    downloadJsonFile(sectionJson, `${section.title}.json`);
  };

  const handleMoveTo = (section: SectionType) => (courseId: string, termId: string) => {
    updateSection({
      id: section.id,
      course_id: courseId,
      term_id: termId,
    });
  };

  const handleToggleArchive = (section: SectionType) => () => {
    storeApi.sections.partial_update({
      id: section.id,
      archived_at: section.archived_at ? null : new Date().toISOString(),
    });
  };

  return (
    <div className="mb-6 border-b border-black-tint-90" role="list">
      <HoverableToolbar reference="section">
        <h3 className={clsx("h3 my-2", isDisabled ? "text-black-tint-55" : "text-black-tint-20")}>
          {title}
        </h3>
      </HoverableToolbar>

      {sectionList.map((section) => (
        <AdminSectionListItem
          canEdit={canEdit}
          eventPrefix={eventPrefix}
          eventProperties={eventProperties}
          key={section.id}
          optionsMenuActions={{
            onSettings: () => handleSectionSettings(section.id),
            onDelete: () => handleSectionDelete(section),
            onDuplicate: () => handleClone(section.id),
            onCopyTo: handleClone,
            onMoveTo: handleMoveTo(section),
            onExport: handleExportSection(section),
            onArchive: handleToggleArchive(section),
            onUnarchive: handleToggleArchive(section),
          }}
          section={section}
        />
      ))}

      {!canEdit && sectionList.length === 0 && (
        <div className="body-s ml-2 w-full py-2 text-black-tint-40">
          {t("section_list_panel.no_sections")}
        </div>
      )}
    </div>
  );
};
